import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {CircularProgress, SvgIcon} from "@mui/material";
import {ReactComponent as More} from "../../../../../../../../assets/images/more.svg";
import classnames from "classnames";
import {Popover} from "../../../../../../../components/app-specific/popover";
import {useTranslation} from "react-i18next";

export const VendorAddressCardActions = {
    edit: 'edit',
    setDefault: 'setDefault',
    remove: 'remove',
};

/**
 * More-menu popover for vendor address cards (edit / set default / remove).
 */
const VendorAddressCardActionsPopover = ({
    actions,
    edit,
    remove: removeProps,
    setDefault,
    disabled,
    settingAsDefault,
}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info.addresses'});
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Runs remove and exposes loading state on the menu item.
     */
    const remove = useCallback(async () => {
        setRemoving(true);
        await removeProps?.();
        if (!isMounted())
            return;
        setRemoving(false);
    }, [isMounted, removeProps]);

    const closeDialogAndPerformAction = (close, action) => {
        close();
        action?.();
    };

    const popoverActions = useMemo(() => {
        const res = [];
        for (const action of actions) {
            switch (action) {
                case VendorAddressCardActions.edit:
                    if (!edit) continue;
                    res.push({
                        title: t('edit'),
                        onClick: (close) => closeDialogAndPerformAction(close, edit),
                        className: '',
                    });
                    break;
                case VendorAddressCardActions.setDefault:
                    if (!setDefault) continue;
                    res.push({
                        loading: settingAsDefault,
                        loadingTitle: t('settingDefault'),
                        title: t('setDefault'),
                        onClick: (close) => closeDialogAndPerformAction(close, setDefault),
                        className: '',
                    });
                    break;
                case VendorAddressCardActions.remove:
                    if (!removeProps) continue;
                    res.push({
                        title: removing ? t('removing') : t('remove'),
                        onClick: remove,
                        className: 'remove',
                    });
                    break;
                default:
                    break;
            }
        }
        return res;
    }, [actions, edit, remove, removeProps, removing, setDefault, settingAsDefault, t]);

    if (!popoverActions.length)
        return null;

    return (
        <Popover
            elevation={2}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
            child={(onEnter) => (
                <IconButton onPressed={onEnter} disabled={disabled} color={'paper.secondary'}>
                    <SvgIcon component={More} className={'icon small'}/>
                </IconButton>
            )}
            popoverChild={(_, close) => (
                <div className={'address-card-popover-actions'}>
                    {
                        popoverActions.map((e) => (
                            <div
                                key={e.title}
                                className={classnames(e.className, 'action-item', {'disabled': e.loading || disabled})}
                                onClick={() => !disabled && !e.loading && e.onClick(close)}
                            >
                                {e.loading && <CircularProgress size={16} className={'me-2'}/>}
                                <p>{e.loading ? (e.loadingTitle ?? e.title) : e.title}</p>
                            </div>
                        ))
                    }
                </div>
            )}
        />
    );
};

export default VendorAddressCardActionsPopover;
